import React, { useState, useCallback, useEffect, useRef } from "react";

// Define interfaces for type safety
interface Coord {
  x: number;
  y: number;
}

interface Props {}

const FaceImage: React.FC<Props> = () => {
  const [position, setPosition] = useState<Coord>({ x: 0, y: 0 });
  const [face, setFace] = useState<number>(4); // Center as default (index 4)
  
  // Using ref prevents rerendering on every mouse move
  const mouse = useRef<Coord>({ x: 0, y: 0 });
  const divElement = useRef<HTMLDivElement>(null);

  const calculateFace = (mouse: Coord, position: Coord): number => {
    const lx = mouse.x < position.x;
    const rx = mouse.x >= position.x + 250;
    const ty = mouse.y < position.y;
    const by = mouse.y >= position.y + 250;

    if (ty) {
      if (lx) return 0; // Top-Left
      if (rx) return 2; // Top-Right
      return 1; // Top
    }

    if (by) {
      if (lx) return 6; // Bottom-Left
      if (rx) return 8; // Bottom-Right
      return 7; // Bottom
    }

    if (lx) return 3; // Left
    if (rx) return 5; // Right
    return 4; // Center
  };

  const handleMouseMove = useCallback((event: MouseEvent) => {
    mouse.current = { x: event.clientX, y: event.clientY };
    
    const nextFace = calculateFace(mouse.current, position);
    
    if (nextFace !== face) {
      setFace(nextFace);
    }
  }, [face, position]);

  const handleResize = useCallback(() => {
    if (divElement && divElement.current) {
      const domCords = divElement.current.getBoundingClientRect();
      
      setPosition({
        x: domCords.x,
        y: domCords.y
      });
    }
  }, []);

  useEffect(() => {
    if (divElement && divElement.current) {
      const domCords = divElement.current.getBoundingClientRect();
      
      setPosition({
        x: domCords.x,
        y: domCords.y
      });
    }

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("resize", handleResize);
    };
  }, [handleMouseMove, handleResize]);

  // Array of your exact image paths
  const imagePaths = [
    "./assets/Left-Top.png",      // 0: Top-Left
    "./assets/Top.png",           // 1: Top
    "./assets/Right-Top.png",     // 2: Top-Right
    "./assets/Left.png",          // 3: Left
    "./assets/Center.png",        // 4: Center
    "./assets/Right.png",         // 5: Right
    "./assets/Left-Bottom.png",   // 6: Bottom-Left
    "./assets/Bottom.png",        // 7: Bottom
    "./assets/Right-Bottom.png"   // 8: Bottom-Right
  ];

  return (
    <div 
      ref={divElement} 
      className="relative w-64 h-64 mx-auto"
      style={{ padding: '12px' }}
    >
      {/* This is our circular mask */}
      <div 
        className="w-full h-full relative overflow-hidden"
        style={{ 
          borderRadius: '50%',
          backgroundColor: 'transparent'
        }}
      >
        {/* Image container */}
        <div 
          style={{ 
            position: 'relative',
            width: '100%',
            height: '100%',
            overflow: 'hidden'
          }}
        >
          {/* Pre-load all images but keep them stacked and hidden except the active one */}
          {imagePaths.map((src, index) => (
            <img
              key={index}
              src={src}
              alt={`Face direction ${index}`}
              style={{ 
                position: 'absolute',
                left: '0',
                width: '100%',
                height: '105%', // Slightly larger than container
                objectFit: 'cover',
                // Position images to show more face and less chest
                objectPosition: 'center 35%', // Shift upward to show less chest
                transform: 'translateY(10%)', // Move images down by 10%
                // Display control
                opacity: face === index ? 1 : 0,
                display: 'block',
                zIndex: face === index ? 2 : 1,
                pointerEvents: 'none'
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default FaceImage;